import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import {
  UserIcon,
  BanknotesIcon,
  IdentificationIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CameraIcon
} from '@heroicons/react/24/outline'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface EmployeeProfile {
  id: number
  employeeNumber: string
  firstName: string
  lastName: string
  middleName?: string
  email?: string
  phone?: string
  nationalId?: string
  department?: string
  position?: string
  hireDate: string
  bankName?: string
  bankAccount?: string
  bankSwiftCode?: string
  taxId?: string
  profilePicture?: string
}

interface EditableProfile {
  phone?: string | undefined
  bankName?: string | undefined
  bankAccount?: string | undefined
  bankSwiftCode?: string | undefined
}

const Profile: React.FC = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState<EditableProfile>({})
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch employee profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ['employee-profile', user?.id],
    queryFn: async (): Promise<EmployeeProfile> => {
      const response = await api.get(`/employees/${user?.id}/profile`)
      return response.data.data
    },
    enabled: !!user?.id
  })

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (updates: EditableProfile) => {
      const response = await api.put(`/employees/${user?.id}/profile`, updates)
      return response.data
    },
    onSuccess: () => {
      toast.success('Profile updated successfully!')
      setIsEditing(false)
      setEditForm({})
      setPreviewImage(null)
      queryClient.invalidateQueries({ queryKey: ['employee-profile', user?.id] })
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || 'Failed to update profile'
      toast.error(errorMessage)
    }
  })

  // Upload profile picture mutation
  const uploadPictureMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('profilePicture', file)
      const response = await api.post(`/employees/${user?.id}/profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    onSuccess: () => {
      toast.success('Profile picture updated successfully!')
      setPreviewImage(null)
      queryClient.invalidateQueries({ queryKey: ['employee-profile', user?.id] })
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || 'Failed to upload profile picture'
      toast.error(errorMessage)
      setPreviewImage(null)
    }
  })

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing
      setIsEditing(false)
      setEditForm({})
      setPreviewImage(null)
    } else {
      // Start editing - populate form with current values
      setIsEditing(true)
      setEditForm({
        phone: profile?.phone || '',
        bankName: profile?.bankName || '',
        bankAccount: profile?.bankAccount || '',
        bankSwiftCode: profile?.bankSwiftCode || ''
      })
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be smaller than 5MB')
        return
      }

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string)
      }
      reader.readAsDataURL(file)

      // Upload immediately
      uploadPictureMutation.mutate(file)
    }
  }

  const handlePictureClick = () => {
    fileInputRef.current?.click()
  }

  const handleSave = () => {
    // Only send fields that have changed
    const updates: EditableProfile = {}

    if (editForm.phone !== profile?.phone) updates.phone = editForm.phone || undefined
    if (editForm.bankName !== profile?.bankName) updates.bankName = editForm.bankName || undefined
    if (editForm.bankAccount !== profile?.bankAccount) updates.bankAccount = editForm.bankAccount || undefined
    if (editForm.bankSwiftCode !== profile?.bankSwiftCode) updates.bankSwiftCode = editForm.bankSwiftCode || undefined

    if (Object.keys(updates).length === 0) {
      toast('No changes to save', { icon: 'ℹ️' })
      setIsEditing(false)
      return
    }

    updateProfileMutation.mutate(updates)
  }

  const handleInputChange = (field: keyof EditableProfile, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">Unable to load profile information</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 mb-4 sm:mb-0">
          <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center">
            <UserIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">My Profile</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              View and update your personal information
            </p>
          </div>
        </div>

        <div className="flex space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={handleEditToggle}
                disabled={updateProfileMutation.isPending}
                className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateProfileMutation.isPending}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              onClick={handleEditToggle}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Profile Picture Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card">
        <div className="px-6 py-6">
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="relative group">
              <div className="h-24 w-24 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 shadow-sm">
                {previewImage || profile?.profilePicture ? (
                  <img
                    src={previewImage || profile?.profilePicture}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <UserIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
              </div>

              {/* Camera overlay */}
              <button
                type="button"
                onClick={handlePictureClick}
                disabled={uploadPictureMutation.isPending}
                className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 disabled:opacity-50"
              >
                <CameraIcon className="h-6 w-6 text-white" />
              </button>

              {/* Loading overlay */}
              {uploadPictureMutation.isPending && (
                <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="h-6 w-6 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
                </div>
              )}
            </div>

            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {profile?.firstName} {profile?.lastName}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {profile?.position} • {profile?.department}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                Employee #{profile?.employeeNumber}
              </p>

              <button
                type="button"
                onClick={handlePictureClick}
                disabled={uploadPictureMutation.isPending}
                className="mt-3 inline-flex items-center px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <CameraIcon className="h-4 w-4 mr-2" />
                {uploadPictureMutation.isPending ? 'Uploading...' : 'Change Photo'}
              </button>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* Profile Sections */}
      <div className="space-y-5">
        {/* Personal Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card">
          <div className="px-6 py-6">
            <div className="flex items-center mb-6">
              <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center mr-3">
                <UserIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Personal Information</h3>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Employee Number</label>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">{profile.employeeNumber}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">{profile.firstName}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">{profile.lastName}</p>
                </div>
              </div>

              {profile.middleName && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Middle Name</label>
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white font-medium">{profile.middleName}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">
                    {profile.email || <span className="text-gray-400 dark:text-gray-500">Not provided</span>}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editForm.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white transition-colors"
                    placeholder="Enter phone number"
                  />
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white font-medium">
                      {profile.phone || <span className="text-gray-400 dark:text-gray-500">Not provided</span>}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card">
          <div className="px-6 py-6">
            <div className="flex items-center mb-6">
              <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mr-3">
                <IdentificationIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Employment Information</h3>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department</label>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">
                    {profile.department || <span className="text-gray-400 dark:text-gray-500">Not specified</span>}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Position</label>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">
                    {profile.position || <span className="text-gray-400 dark:text-gray-500">Not specified</span>}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Hire Date</label>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">
                    {new Date(profile.hireDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {profile.nationalId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">National ID</label>
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white font-medium">{profile.nationalId}</p>
                  </div>
                </div>
              )}

              {profile.taxId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tax ID</label>
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white font-medium">{profile.taxId}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Banking Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card">
          <div className="px-6 py-6">
            <div className="flex items-center mb-6">
              <div className="h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mr-3">
                <BanknotesIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Banking Information</h3>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bank Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.bankName || ''}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white transition-colors"
                    placeholder="Enter bank name"
                  />
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white font-medium">
                      {profile.bankName || <span className="text-gray-400 dark:text-gray-500">Not provided</span>}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Account Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.bankAccount || ''}
                    onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white transition-colors"
                    placeholder="Enter account number"
                  />
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white font-medium">
                      {profile.bankAccount ? '****' + profile.bankAccount.slice(-4) : <span className="text-gray-400 dark:text-gray-500">Not provided</span>}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">SWIFT Code</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.bankSwiftCode || ''}
                    onChange={(e) => handleInputChange('bankSwiftCode', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white transition-colors"
                    placeholder="Enter SWIFT code"
                  />
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white font-medium">
                      {profile.bankSwiftCode || <span className="text-gray-400 dark:text-gray-500">Not provided</span>}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Update Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <IdentificationIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Profile Update Guidelines</h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                <ul className="list-disc list-inside space-y-1">
                  <li>You can update your phone number and banking information</li>
                  <li>Changes to personal details (name, email, etc.) require HR approval</li>
                  <li>Banking information is used for salary payments</li>
                  <li>Contact HR for assistance with employment details</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile